// ─── BizConnect360 Master Agent Participation Agreement ──────────────────────
// Canonical metadata for the agreement an associate signs.
//
// HISTORY. Two different agreement wordings used to be hardcoded as string
// literals in JoinAgent.tsx and RepDashboard.tsx, and they had drifted apart,
// so an associate was shown one document at registration and a different one
// at their first dashboard login. v3.0 ends that: the client supplied a single
// approved master agreement (PDF, 14 September 2026) and it is now the only
// agreement in the system.
//
// WHY THERE IS NO AGREEMENT TEXT IN THIS FILE ANY MORE. The approved document
// is a 13-page PDF with bullets, sub-numbering (for example Section 19.3.2) and
// its own signature block. Re-typesetting that as text would mean the copy an
// associate signs is not visually the document the client approved, which for a
// superseding master agreement is a real risk. So the PDF itself is the
// canonical artifact: it is served to the associate for reading, and their
// signature is stamped onto it. See server/agreementPdf.ts.
//
// The document expressly supersedes any prior BizConnect360 agent agreement
// (see its preamble and Section 27), which is why existing associates are asked
// to re-sign rather than being grandfathered on a v1.0 checkbox acceptance.

/**
 * Document issue. Bump on ANY change to the template PDF.
 *
 * v1.0  bare "I Agree" checkbox, no name, no signature, no stored document.
 * v2.0  typed-name signature (built, never deployed, superseded before release).
 * v3.0  the 2026-09-14 PDF, signed with a drawn signature plus printed name
 *       plus server-applied date. SENT TO BC360 IN ERROR by the client - see
 *       v3.1. 53 real associates signed this before it was caught: 49 via the
 *       drawing pad (which had a separate, since-fixed bug of its own -
 *       exported near-white ink on a white page) and 4 by uploading an image.
 * v3.1  the client's corrected replacement, supplied 2026-09-17. Textually
 *       almost identical to v3.0 (one preamble sentence added, the date
 *       corrected to September 16), but that sentence shifts every line below
 *       it down by ~11.8pt, which moved the acknowledgment block's exact
 *       position - the v3.0 pixel coordinates could not be reused as-is; see
 *       agreementPdf.ts. Bumped rather than silently swapping the file under
 *       the same "3.0" label, because two different template files sharing
 *       one version string would be exactly the kind of ambiguity a signed
 *       legal document must not have. Every v3.0 acceptance - the 254 legacy
 *       ones AND the 53 real signatures against the erroneous file - fails
 *       the version check and is asked to sign again under v3.1.
 */
export const AGREEMENT_VERSION = "3.1";

export const AGREEMENT_TITLE = "BizConnect360 Master Agent Participation Agreement";

/** Date the client approved this wording, from the document's own header. */
export const AGREEMENT_LAST_UPDATED = "September 16, 2026";

/**
 * Template filename, as committed under server/assets/.
 *
 * Kept as a stable contract name because the runtime depends on it. The
 * SHA-256 of these exact bytes is recorded against every signature, so a
 * signed record can always be checked against the document that produced it.
 */
export const AGREEMENT_TEMPLATE_FILENAME =
  "BizConnect360_Master_Agent_Participation_Agreement_REVISED_2026-09-16.pdf";

/** Public route the associate's browser reads the agreement from. */
export const AGREEMENT_TEMPLATE_URL = "/api/agreement/master.pdf";

/**
 * Shown directly above the signing controls.
 *
 * Deliberately paraphrases Section 26 (Electronic Signatures and Acceptance)
 * of the agreement itself rather than inventing new legal language.
 */
export const AGREEMENT_ESIGN_DISCLOSURE =
  "By drawing your signature and typing your full legal name below, you are signing this Agreement " +
  "electronically. As stated in Section 26 of this Agreement, electronic signatures and other lawful " +
  "forms of electronic assent have the same force and effect as original signatures. By signing, you " +
  "confirm that you have read, understand, and agree to be legally bound by its terms.";

/** Punctuation and digits stripped when checking a typed name has real content. */
const NON_NAME_CHARS = /[\s\d`~!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g;

/**
 * Whether a typed full legal name is acceptable.
 *
 * Lives in shared/ so the browser's "enable the button" check and the server's
 * validation are literally the same function. If they diverged, an associate
 * could be shown an enabled Sign button and then rejected by the server, or
 * worse, the reverse.
 *
 * Deliberately does NOT use the \p{L} unicode property escape: this project's
 * TypeScript target predates it. Stripping digits and punctuation instead still
 * accepts accented and non-Latin names, which a naive /[A-Za-z]/ test would not.
 */
export function isValidPrintedName(value: string): boolean {
  const v = value.trim();
  if (v.length < 2 || v.length > 200) return false;
  return v.replace(NON_NAME_CHARS, "").length >= 2;
}

/**
 * The one and only ink color a signature is ever exported in, regardless of
 * how it was produced (drawn or uploaded) or what color it was shown in on
 * screen while the signer was making it.
 *
 * This exists because of a real incident: SignaturePad drew near-white ink on
 * screen (so it would be visible against the dark pages it lives on), and that
 * exact color was what got exported and stamped onto the white agreement PDF
 * - producing a signature that was essentially invisible. Both the drawing
 * pad's export and the upload path's background-removal normalization now
 * reference this single constant, so the two can never drift into different
 * colors again, and so a future theme change to either surface cannot
 * silently affect what gets printed on the contract.
 */
export const SIGNATURE_EXPORT_INK = { r: 15, g: 20, b: 45, hex: "#0f142d" } as const;

/** Upper bound on a submitted signature PNG, before base64 expansion. */
export const SIGNATURE_MAX_BYTES = 512 * 1024;

/** Largest file the upload option will accept before processing. */
export const SIGNATURE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/** Image types the upload option accepts. HEIC is excluded deliberately: */
/** browsers cannot decode it to a canvas, so it could never be processed. */
export const SIGNATURE_UPLOAD_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/bmp";

/** How the associate produced their signature. Recorded for the audit trail. */
export type SignatureSource = "drawn" | "uploaded";

/**
 * Whether a submitted signature is a usable PNG.
 *
 * Checked on both sides of the wire from this one function.
 *
 * Two floors matter:
 *  - The PNG magic bytes, so only a real PNG is ever stamped onto a contract.
 *    Checked as a base64 prefix rather than by decoding: every PNG begins with
 *    the same 8-byte signature, which always encodes to "iVBORw0K".
 *  - A minimum size, because a canvas the signer merely tapped once, or an
 *    upload that processed down to nothing, still produces a structurally
 *    valid PNG that is visually EMPTY. An empty signature box on a contract is
 *    worse than no contract, because the document still looks signed.
 */
export function isValidSignatureDataUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const m = /^data:image\/png;base64,([A-Za-z0-9+/=]+)$/.exec(value.trim());
  if (!m) return false;
  const base64 = m[1];
  if (!base64.startsWith("iVBORw0K")) return false;
  const bytes = Math.floor((base64.length * 3) / 4);
  return bytes > 1024 && bytes <= SIGNATURE_MAX_BYTES;
}
